import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { FIRM } from "@/data/content";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/insights", label: "Insights" },
    { to: "/capabilities", label: "Capabilities" },
    { to: "/contact", label: "Contact" },
];

const linkSlug = (to) => (to === "/" ? "home" : to.slice(1));

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-800/60 bg-[#070a0f]/85 backdrop-blur-xl">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6 lg:px-12">
                <Link
                    to="/"
                    data-testid="nav-brand-link"
                    className="group flex items-center gap-3"
                    onClick={() => setOpen(false)}
                >
                    <span className="flex h-9 w-9 items-center justify-center border border-slate-700 font-serif text-lg text-slate-200 transition-colors duration-300 group-hover:border-blue-500 group-hover:text-white">
                        {FIRM.monogram}
                    </span>
                    <span className="leading-tight">
                        <span className="block font-serif text-lg text-slate-100">{FIRM.short}</span>
                        <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                            Strategies LLC
                        </span>
                    </span>
                </Link>

                <nav aria-label="Primary" className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${linkSlug(l.to)}-link`}
                            className={({ isActive }) =>
                                `group relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    isActive ? "text-slate-100" : "text-slate-400 hover:text-slate-200"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    <span
                                        className={`absolute -bottom-2 left-0 h-px bg-blue-500 transition-all duration-300 ${
                                            isActive ? "w-full" : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-10 w-10 items-center justify-center border border-slate-800 text-slate-300 lg:hidden"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        aria-label="Mobile"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-slate-800/60 bg-[#070a0f]/95 lg:hidden"
                    >
                        <div className="flex flex-col px-6 py-6">
                            {LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.4 }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`nav-mobile-${linkSlug(l.to)}-link`}
                                        onClick={() => setOpen(false)}
                                        className={({ isActive }) =>
                                            `flex items-baseline justify-between border-b border-slate-800/60 py-4 font-serif text-2xl ${
                                                isActive || (l.to === "/" && pathname === "/")
                                                    ? "text-slate-100"
                                                    : "text-slate-400"
                                            }`
                                        }
                                    >
                                        {l.label}
                                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
                                            0{i + 1}
                                        </span>
                                    </NavLink>
                                </motion.div>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
